/**
 * VistoeraTASK-00633 — node-select auth matrix.
 *
 * Left: compact function-node list (click to select).
 * Right: Provider channels for the selected node only (from that row's
 * catalog/mapping channels — never a global cross-product of all providers).
 *
 * Draft grants remain plan-scoped in memory; switching nodes does not discard.
 */
import { Checkbox, Empty, Switch, Table, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useMemo } from 'react'
import { channelCellMode, grantKey, type MatrixChannelCell, type MatrixRow } from './authMatrixDraft'
import './auth-matrix-table.css'

export type AuthMatrixTableProps = {
  rows: MatrixRow[]
  draft: Record<string, 'ALLOW' | 'DENY'>
  selectedNodeId: string | null
  onSelectNode: (nodeId: string) => void
  onNodeToggle: (nodeId: string, allowed: boolean) => void
  onChannelToggle: (channelId: string, allowed: boolean) => void
  loading?: boolean
  readOnly?: boolean
}

function ChannelPanel({
  row,
  draft,
  onChannelToggle,
  readOnly,
}: {
  row: MatrixRow
  draft: Record<string, 'ALLOW' | 'DENY'>
  onChannelToggle: (channelId: string, allowed: boolean) => void
  readOnly?: boolean
}) {
  const nodeAllowed = draft[grantKey('node', row.node.subject_id)] === 'ALLOW'
  // Only this node's mapped channels (never global provider cross-product).
  const channels = row.channels || []

  if (channels.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="当前节点暂无已映射的执行通道"
        style={{ margin: '24px 0' }}
      />
    )
  }

  return (
    <div className="amx-channel-panel-scroll">
      <div className="amx-channel-panel-grid">
        {channels.map((ch: MatrixChannelCell) => {
          const mode = channelCellMode(row, ch.subject_id, nodeAllowed)
          const checked = draft[grantKey('channel', ch.subject_id)] === 'ALLOW'
          const label = ch.display_name || ch.subject_id

          if (mode === 'blocked_global') {
            return (
              <Tooltip key={ch.subject_id} title="通道不可用 / 全站已关闭">
                <label className="amx-channel-chip amx-channel-chip-disabled">
                  <Checkbox checked={false} disabled />
                  <span className="amx-channel-chip-label">{label}</span>
                </label>
              </Tooltip>
            )
          }
          if (mode === 'blocked_node') {
            return (
              <Tooltip key={ch.subject_id} title="节点关闭时通道不可用（保留已保存配置）">
                <label className="amx-channel-chip amx-channel-chip-disabled">
                  <Checkbox checked={checked} disabled />
                  <span className="amx-channel-chip-label">{label}</span>
                </label>
              </Tooltip>
            )
          }
          return (
            <label key={ch.subject_id} className="amx-channel-chip">
              <Checkbox
                checked={checked}
                disabled={readOnly}
                onChange={(e) => onChannelToggle(ch.subject_id, e.target.checked)}
              />
              <Tooltip title={label}>
                <span className="amx-channel-chip-label">{label}</span>
              </Tooltip>
            </label>
          )
        })}
      </div>
    </div>
  )
}

export default function AuthMatrixTable({
  rows,
  draft,
  selectedNodeId,
  onSelectNode,
  onNodeToggle,
  onChannelToggle,
  loading,
  readOnly,
}: AuthMatrixTableProps) {
  const selectedRow = useMemo(
    () => rows.find((r) => r.node.subject_id === selectedNodeId) ?? null,
    [rows, selectedNodeId],
  )

  const columns: ColumnsType<MatrixRow> = useMemo(
    () => [
      {
        // TASK-00670 — catalog display_name; never raw category (production/content).
        title: '节点类型',
        key: 'node',
        width: 168,
        ellipsis: true,
        render: (_: unknown, row) => {
          const selected = row.node.subject_id === selectedNodeId
          const label = (row.node.display_name || '').trim() || row.node.subject_id
          return (
            <button
              type="button"
              className={`amx-node-select${selected ? ' amx-node-select-active' : ''}`}
              onClick={() => onSelectNode(row.node.subject_id)}
              title={label}
              data-node-id={row.node.subject_id}
            >
              <span className="amx-node-name">{label}</span>
              {!row.node.globally_available ? (
                <Typography.Text type="secondary" className="amx-node-hint">
                  全站已关闭
                </Typography.Text>
              ) : null}
            </button>
          )
        },
      },
      {
        title: '全局开关',
        key: 'node_switch',
        width: 90,
        align: 'center',
        render: (_: unknown, row) => {
          const allowed = draft[grantKey('node', row.node.subject_id)] === 'ALLOW'
          const globalOk = Boolean(row.node.globally_available) && row.node.grantable !== false
          return (
            <Tooltip title={!globalOk ? '全站已关闭，套餐无法授权' : undefined}>
              <Switch
                size="small"
                checked={allowed && globalOk}
                disabled={readOnly || !globalOk}
                onChange={(v) => onNodeToggle(row.node.subject_id, v)}
              />
            </Tooltip>
          )
        },
      },
    ],
    [draft, onNodeToggle, onSelectNode, readOnly, selectedNodeId],
  )

  return (
    <div className="amx-split">
      <div className="amx-split-nodes">
        <Table
          className="amx-table amx-table-nodes"
          size="small"
          rowKey={(r) => r.node.subject_id}
          loading={loading}
          pagination={false}
          scroll={{ y: 420 }}
          columns={columns}
          dataSource={rows}
          rowClassName={(row) =>
            row.node.subject_id === selectedNodeId ? 'amx-row-selected' : ''
          }
          onRow={(row) => ({
            onClick: () => onSelectNode(row.node.subject_id),
          })}
          locale={{ emptyText: '暂无功能节点目录' }}
        />
      </div>
      <div className="amx-split-channels">
        <div className="amx-channels-header">
          <Typography.Text strong>
            {selectedRow
              ? `${selectedRow.node.display_name || selectedRow.node.subject_id} · 可用通道`
              : '可用通道'}
          </Typography.Text>
          <Typography.Text type="secondary" className="amx-channels-hint">
            {selectedRow
              ? '仅显示已映射到本节点的 Provider / Execution Plan'
              : '请点击左侧功能节点以配置通道授权'}
          </Typography.Text>
        </div>
        {selectedRow ? (
          <ChannelPanel
            row={selectedRow}
            draft={draft}
            onChannelToggle={onChannelToggle}
            readOnly={readOnly}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="选择功能节点后显示对应通道"
            style={{ margin: '40px 0' }}
          />
        )}
      </div>
    </div>
  )
}
